import type { BadgeProps } from "@rumbledirect/ui-v2";
import type {
  VehicleContractStatus,
  VehicleContractType,
} from "@/vehicleContracts/vehicleContract";
import type {
  ContractEndSource,
  ContractMonitorStatus,
  ContractMonitorStatusFilter,
} from "./contractMonitor";

/**
 * Dutch labels for a contract's lifecycle, mirroring the backend's status
 * translations. Records carry their own `label`; this map serves the filter
 * and the options of the vehicle contract form.
 */
export const CONTRACT_STATUS_LABELS: Record<VehicleContractStatus, string> = {
  pending: "In afwachting",
  active: "Actief",
  expired: "Verlopen",
  terminated: "Beëindigd",
};

/**
 * Dutch labels for how the vehicle is held, for the options of the vehicle
 * contract form. The lease forms keep their English names because that is what
 * the contracts themselves say; only the two that are not lease are translated.
 */
export const CONTRACT_TYPE_LABELS: Record<
  NonNullable<VehicleContractType>,
  string
> = {
  operational_lease: "Operational lease",
  financial_lease: "Financial lease",
  short_lease: "Short lease",
  rental: "Huur",
  owned: "Eigendom",
  other: "Overig",
};

/** Badge tone per status, mirroring {@link CONTRACT_STATUS_LABELS}. */
export const CONTRACT_STATUS_VARIANTS: Record<
  VehicleContractStatus,
  NonNullable<BadgeProps["variant"]>
> = {
  pending: "info",
  active: "success",
  expired: "warning",
  terminated: "neutral",
};

/** Badge tone for a monitor row's status; anything unexpected stays neutral. */
export function getContractStatusVariant(
  status: ContractMonitorStatus,
): NonNullable<BadgeProps["variant"]> {
  return isContractMonitorStatusFilter(status.value)
    ? CONTRACT_STATUS_VARIANTS[status.value]
    : "neutral";
}

/**
 * Dutch labels for the date that produced the effective end, mirroring the
 * backend's `ContractEndSource` translations.
 *
 * A contract carries up to three end dates and the monitor shows only the one
 * that won, so this label is what tells an early return apart from a planned
 * end without widening the table by two columns.
 */
export const CONTRACT_END_SOURCE_LABELS: Record<ContractEndSource, string> = {
  return_at: "Ingeleverd op",
  expected_end_at: "Verwachte einddatum",
  end_at: "Einddatum",
  open_ended: "Geen einddatum",
};

/** The statuses offered in the filter, in lifecycle order. */
export const CONTRACT_STATUS_FILTER_OPTIONS: readonly {
  readonly value: ContractMonitorStatusFilter;
  readonly label: string;
}[] = [
  { value: "pending", label: CONTRACT_STATUS_LABELS.pending },
  { value: "active", label: CONTRACT_STATUS_LABELS.active },
  { value: "expired", label: CONTRACT_STATUS_LABELS.expired },
];

/** Type guard for a status coming out of the URL's search params. */
export function isContractMonitorStatusFilter(
  value: unknown,
): value is ContractMonitorStatusFilter {
  return CONTRACT_STATUS_FILTER_OPTIONS.some(
    (option) => option.value === value,
  );
}
